import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Search, Shield, Users as UsersIcon, User, Phone, Mail, MapPin, Sun, Moon } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useLazySearchCustomersAllBranchesQuery,
} from "../customersApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateCustomers, useCanEditCustomers, useCanViewLocation } from "../useCustomerPermissions";
import LinkedCustomersPanel from "../components/LinkedCustomersPanel";
import BranchAvailabilityPanel from "../components/BranchAvailabilityPanel";
import CustomerQrPanel from "../components/CustomerQrPanel";
import CustomerLocationPanel from "../components/CustomerLocationPanel";
import CustomerSitesPanel from "../components/CustomerSitesPanel";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const emptyForm = {
  branchId: "",
  isActive: true,
  custName: "",
  signalCode: "",
  address1: "",
  address2: "",
  location: "",
  country: "",
  contactPerson: "",
  telephone: "",
  fax: "",
  mobileNo: "",
  email: "",
  web: "",
  creditDays: "",
  paymentTerms: "",
};

// Mirrors texas_master's Frm_Customer "Details" panel, now against the live
// /api/v1/masters/customers endpoints (handoff §9.7). Guarding Assignments
// and Bulk Entry are dedicated buttons here, not tabs or an expand panel.
// Customer Groups were dropped (confirmed not implemented API-side either).
const CustomerForm = () => {
  const { branchId: routeBranchId, custId } = useParams();
  const navigate = useNavigate();
  const canCreate = useCanCreateCustomers();
  const canEdit = useCanEditCustomers();
  const canViewLocation = useCanViewLocation();
  const isEditing = Boolean(custId);
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
    error: detailError,
  } = useGetCustomerQuery(
    { branchId: routeBranchId, custId },
    { skip: !isEditing }
  );
  const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation();
  const [triggerDuplicateSearch] = useLazySearchCustomersAllBranchesQuery();
  // Live typeahead (as-you-type, debounced) under the Name field - purely
  // informational, links open in a new tab so the in-progress form isn't
  // lost. The search button pins the same query's results into a
  // persistent card box at the bottom, styled like "Linked in Other
  // Branches" (handoff §9.7/§13.2), for a fuller look than the dropdown.
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [isSearchingDuplicates, setIsSearchingDuplicates] = useState(false);
  const typeaheadTimer = useRef(null);
  // Kenya street-search assist for Location (handoff §9.7) - deliberately a
  // frontend-only concern, no backend endpoint. Nominatim is free/unlimited
  // vs. Google Places' billing requirement (explicit decision, see project
  // memory). The 500ms debounce plus one in-flight request at a time
  // (aborting the previous one) keeps this comfortably under Nominatim's
  // 1 req/sec usage-policy cap; the browser's own Referer header is what
  // identifies this app to Nominatim (fetch() can't set a custom
  // User-Agent from a browser). Picking a suggestion just fills the text
  // field as-is - the API does no geocoding, and this never touches the
  // customer's GPS pin (that's write-only via a QR scan, §19).
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationOpen, setLocationOpen] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const locationTimer = useRef(null);
  const locationAbortRef = useRef(null);

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [form, setForm] = useState(emptyForm);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  const defaultBranchId = useDefaultFormBranch(availableBranches);
  useEffect(() => {
    if (!isEditing && defaultBranchId !== "") setForm((f) => (f.branchId ? f : { ...f, branchId: defaultBranchId }));
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEditing && detail?.customer) {
      setForm({ ...emptyForm, ...detail.customer });
    } else if (!isEditing) {
      setForm({
        ...emptyForm,
        branchId: defaultBranchId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, detail]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, custName: value }));
    if (isEditing) return;
    if (typeaheadTimer.current) clearTimeout(typeaheadTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setTypeaheadResults([]);
      setTypeaheadOpen(false);
      return;
    }
    typeaheadTimer.current = setTimeout(async () => {
      // includeInactive: true - a deactivated customer is exactly the kind
      // of duplicate this check exists to catch (direct instruction,
      // 2026-09-07); every other consumer of this search keeps active-only.
      const { data = [] } = await triggerDuplicateSearch({ query: trimmed, includeInactive: true, crossBranch: true });
      setTypeaheadResults(data);
      setTypeaheadOpen(data.length > 0);
    }, 300);
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, location: value }));
    if (locationTimer.current) clearTimeout(locationTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setLocationSuggestions([]);
      setLocationOpen(false);
      return;
    }
    locationTimer.current = setTimeout(async () => {
      if (locationAbortRef.current) locationAbortRef.current.abort();
      const controller = new AbortController();
      locationAbortRef.current = controller;
      setIsSearchingLocation(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=5&q=${encodeURIComponent(trimmed)}`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();
        setLocationSuggestions(Array.isArray(data) ? data : []);
        setLocationOpen(true);
      } catch (err) {
        if (err.name !== "AbortError") setLocationSuggestions([]);
      } finally {
        setIsSearchingLocation(false);
      }
    }, 500);
  };

  const handlePickLocation = (suggestion) => {
    setForm((prev) => ({ ...prev, location: suggestion.display_name }));
    setLocationSuggestions([]);
    setLocationOpen(false);
  };

  // Duplicate-detection suggestion (handoff §9.7/§13.2) - non-blocking,
  // explicit (button click, not a hidden blur trigger). Never stops the
  // save; just flags possible existing customers in other branches so
  // staff can link them afterward instead of accidentally duplicating.
  const handleSearchDuplicates = async () => {
    const name = form.custName.trim();
    if (name.length < 2) {
      toast.error("Type at least 2 characters of the name first");
      return;
    }
    setIsSearchingDuplicates(true);
    try {
      const { data = [] } = await triggerDuplicateSearch({ query: name, includeInactive: true, crossBranch: true });
      setDuplicateMatches(data);
      setTypeaheadOpen(false);
      if (data.length === 0) toast.success("No matching names found in other branches");
    } finally {
      setIsSearchingDuplicates(false);
    }
  };

  const handleSave = async () => {
    if (!form.custName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!isEditing && !form.branchId) {
      toast.error("Branch is required");
      return;
    }
    // Mandatory on Create only (handoff §9.7, added 2026-08-05) - the API
    // rejects a blank/whitespace location with a 400 here, but deliberately
    // does NOT enforce this on Update (71.6% of existing customers currently
    // have a blank location, so retroactively requiring it there would block
    // editing most of the existing base).
    if (!isEditing && !form.location.trim()) {
      toast.error("Location is required");
      return;
    }
    // Mandatory on Create only, same reasoning/precedent as Location above -
    // a web-created customer with no way to reach them (report: "no contact
    // info") should no longer be possible, but a large share of the existing
    // customer base predates this rule, so Edit stays unblocked for them.
    if (!isEditing && !form.contactPerson.trim()) {
      toast.error("Contact Person is required");
      return;
    }
    if (!isEditing && !form.mobileNo.trim()) {
      toast.error("Mobile No is required");
      return;
    }
    if (!isEditing && !form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!isEditing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Enter a valid email address");
      return;
    }
    // latitude/longitude/locationUpdatedAt/locationSourceUserId/
    // locationSourceName ride along in `form` (spread from the detail
    // fetch, handoff §19) but are read-only via the web - only a Flutter QR
    // scan writes them - so they're deliberately excluded from the save body.
    const {
      latitude, longitude, locationUpdatedAt, locationSourceUserId, locationSourceName,
      ...formForSave
    } = form;
    const body = {
      ...formForSave,
      branchId: Number(form.branchId),
      creditDays: form.creditDays === "" ? null : Number(form.creditDays),
    };
    try {
      if (isEditing) {
        await updateCustomer({ branchId: routeBranchId, custId, ...body }).unwrap();
        toast.success("Customer saved");
        navigate(paths.customerEdit(routeBranchId, custId), { replace: true });
      } else {
        const created = await createCustomer(body).unwrap();
        toast.success("Customer saved");
        navigate(paths.customerEdit(created.customer.branchId, created.customer.custId), {
          replace: true,
        });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save customer");
    }
  };

  if (isEditing && isDetailError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.customers} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Customers
          </Link>
          <div className="alert alert-danger">
            Could not load this customer ({detailError?.message || "server error"}). Try again,
            or report branch {routeBranchId} / custId {custId} to the API team.
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Customers.Create/.Edit are separate permissions (§13.3) - creating a new
  // customer needs Create, editing an existing one needs Edit.
  const readOnly = isEditing ? !canEdit : !canCreate;

  const activeGuardAssignments = (detail?.assignments || []).filter((a) => Number(a.isActive));
  const activeAssignmentCount = activeGuardAssignments.length;
  const dayAssignmentCount = activeGuardAssignments.filter((a) => a.dayNight === "Day").length;
  const nightAssignmentCount = activeGuardAssignments.filter((a) => a.dayNight === "Night").length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header flex-wrap">
          <div className="add-item d-flex" style={{ minWidth: 0 }}>
            {/* Customer names can run 250-300 characters - wrap rather than
                let the title blow out the layout or push the action buttons off. */}
            <div className="page-title" style={{ maxWidth: 700, wordBreak: "break-word" }}>
              <Link to={paths.customers} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Customers
              </Link>
              <h4>{isEditing ? "Edit Customer" : "Add New Customer"}</h4>
            </div>
          </div>
          {isEditing && (
            <div className="page-btn">
              <button
                type="button"
                className="btn btn-outline-primary me-2"
                onClick={() => navigate(paths.customerGuarding(routeBranchId, custId))}
              >
                <Shield size={16} className="me-2" />
                Guarding Assignments
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate(paths.customerBulkGuards(routeBranchId, custId))}
              >
                <UsersIcon size={16} className="me-2" />
                Bulk Entry
              </button>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="card mb-4">
            <div className="card-body d-flex align-items-center gap-3">
              <span className="master-detail-avatar master-detail-avatar-lg">
                {(form.custName || "?").trim().slice(0, 2).toUpperCase()}
              </span>
              <div className="flex-grow-1">
                <h5 className="mb-0">{form.custName || "-"}</h5>
                <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                  <span className="d-inline-flex align-items-center">
                    <User size={14} className="me-1" />
                    {form.contactPerson || "-"}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <Phone size={14} className="me-1" />
                    {form.mobileNo || "-"}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <Mail size={14} className="me-1" />
                    {form.email || "-"}
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <MapPin size={14} className="me-1" />
                    {form.location || "-"}
                  </span>
                  {/* Reuses the same detail.assignments this customer's own
                      GuardingAssignmentsPage already fetches (isActive/
                      dayNight, "Both" never lands here - it's fanned out
                      into separate Day/Night rows on save) - no new API
                      call, just a different summary of data already on hand. */}
                  <Link
                    to={paths.customerGuarding(routeBranchId, custId)}
                    className="d-inline-flex align-items-center text-muted"
                  >
                    <Shield size={14} className="me-1" />
                    {activeAssignmentCount} active
                  </Link>
                  <span className="d-inline-flex align-items-center">
                    <Sun size={14} className="me-1 text-warning" />
                    {dayAssignmentCount} day
                  </span>
                  <span className="d-inline-flex align-items-center">
                    <Moon size={14} className="me-1 text-primary" />
                    {nightAssignmentCount} night
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. {isEditing ? "Editing" : "Creating"} customers requires
            the Customers.{isEditing ? "Edit" : "Create"} permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-12 mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={readOnly}
                  />
                  <label className="form-check-label" htmlFor="isActive">Active</label>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={form.branchCode || branches.find((b) => b.branchId === form.branchId)?.branchCode || ""}
                    readOnly
                    disabled
                  />
                ) : (
                  <select
                    className="form-control"
                    value={form.branchId}
                    onChange={setField("branchId")}
                    disabled={readOnly}
                  >
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-4 mb-3" style={{ position: "relative" }}>
                <label className="form-label">Name</label>
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    value={form.custName}
                    onChange={handleNameChange}
                    onFocus={() => setTypeaheadOpen(typeaheadResults.length > 0)}
                    onBlur={() => setTimeout(() => setTypeaheadOpen(false), 150)}
                    disabled={readOnly}
                    autoComplete="off"
                  />
                  {!isEditing && (
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-shrink-0"
                      onClick={handleSearchDuplicates}
                      disabled={readOnly || isSearchingDuplicates}
                      title="Search this name across all branches"
                    >
                      <Search size={16} />
                    </button>
                  )}
                </div>
                {typeaheadOpen && typeaheadResults.length > 0 && (
                  <div
                    className="card position-absolute w-100 mt-1"
                    style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
                  >
                    <div className="list-group list-group-flush">
                      {typeaheadResults.map((m) => (
                        <Link
                          key={`${m.branchId}-${m.custId}`}
                          to={paths.customerEdit(m.branchId, m.custId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="list-group-item list-group-item-action py-2"
                        >
                          <div className="fw-semibold d-flex align-items-center gap-2">
                            {m.custName}
                            {m.isActive === false && <span className="badge badge-linedanger">Inactive</span>}
                          </div>
                          <div className="text-muted small">
                            {m.branchCode}{m.contactPerson ? ` · ${m.contactPerson}` : ""}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Contact Person{!isEditing && <span className="text-danger"> *</span>}
                </label>
                <input type="text" className="form-control" value={form.contactPerson || ""} onChange={setField("contactPerson")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Signal Code</label>
                <input type="text" className="form-control" value={form.signalCode || ""} onChange={setField("signalCode")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Address 1</label>
                <input type="text" className="form-control" value={form.address1 || ""} onChange={setField("address1")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Address 2</label>
                <input type="text" className="form-control" value={form.address2 || ""} onChange={setField("address2")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3" style={{ position: "relative" }}>
                <label className="form-label">
                  Location{!isEditing && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={form.location || ""}
                  onChange={handleLocationChange}
                  onFocus={() => setLocationOpen(locationSuggestions.length > 0)}
                  onBlur={() => setTimeout(() => setLocationOpen(false), 150)}
                  disabled={readOnly}
                  autoComplete="off"
                  placeholder={!isEditing ? "Start typing a street/area in Kenya..." : ""}
                />
                {isSearchingLocation && <div className="text-muted small mt-1">Searching...</div>}
                {locationOpen && locationSuggestions.length > 0 && (
                  <div
                    className="card position-absolute w-100 mt-1"
                    style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
                  >
                    <div className="list-group list-group-flush">
                      {locationSuggestions.map((s) => (
                        <button
                          key={s.place_id}
                          type="button"
                          className="list-group-item list-group-item-action py-2 text-start"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handlePickLocation(s)}
                        >
                          {s.display_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Country</label>
                <input type="text" className="form-control" value={form.country || ""} onChange={setField("country")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Email{!isEditing && <span className="text-danger"> *</span>}
                </label>
                <input type="email" className="form-control" value={form.email || ""} onChange={setField("email")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Telephone</label>
                <input type="text" className="form-control" value={form.telephone || ""} onChange={setField("telephone")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Fax</label>
                <input type="text" className="form-control" value={form.fax || ""} onChange={setField("fax")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Mobile No{!isEditing && <span className="text-danger"> *</span>}
                </label>
                <input type="text" className="form-control" value={form.mobileNo || ""} onChange={setField("mobileNo")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Web</label>
                <input type="text" className="form-control" value={form.web || ""} onChange={setField("web")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Credit Days</label>
                <input type="number" className="form-control" value={form.creditDays ?? ""} onChange={setField("creditDays")} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Payment Terms</label>
                <input type="text" className="form-control" value={form.paymentTerms || ""} onChange={setField("paymentTerms")} disabled={readOnly} />
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.customers)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {!isEditing && duplicateMatches.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Possible Matches in Other Branches</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Dismiss"
                  onClick={() => setDuplicateMatches([])}
                />
              </div>
              <p className="text-muted small">
                These look similar to what you're creating - not necessarily the same customer.
                Review, and if it really is the same one, save this record first, then use "Linked
                in Other Branches" on the new record to link them.
              </p>
              <div className="row">
                {duplicateMatches.map((m) => (
                  <div className="col-md-6 mb-2" key={`${m.branchId}-${m.custId}`}>
                    <div className="border rounded p-2">
                      <Link to={paths.customerEdit(m.branchId, m.custId)} target="_blank" rel="noopener noreferrer">
                        <strong>{m.custName}</strong>
                      </Link>
                      {m.isActive === false && <span className="badge badge-linedanger ms-2">Inactive</span>}
                      <div className="text-muted small">{m.branchCode}</div>
                      {m.contactPerson && <div className="text-muted small">{m.contactPerson}</div>}
                      {(m.telephone || m.mobileNo) && (
                        <div className="text-muted small">{m.telephone || m.mobileNo}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {isEditing && (
          <BranchAvailabilityPanel
            branchId={routeBranchId}
            custId={custId}
            homeBranchCode={form.branchCode}
            readOnly={readOnly}
          />
        )}

        {isEditing && (
          <LinkedCustomersPanel branchId={routeBranchId} custId={custId} readOnly={readOnly} />
        )}

        {isEditing && (
          <CustomerSitesPanel
            branchId={routeBranchId}
            custId={custId}
            custName={form.custName}
            custCode={form.custCode}
            customerLocation={form.location}
            locations={detail?.locations}
            readOnly={readOnly}
          />
        )}

        {isEditing && (
          <CustomerQrPanel
            branchId={routeBranchId}
            custId={custId}
            custName={form.custName}
            location={form.location}
            custCode={form.custCode}
            branchCode={form.branchCode}
            primaryLocation={detail?.locations?.find((l) => l.isPrimary)}
          />
        )}

        {isEditing && canViewLocation && (
          <CustomerLocationPanel
            branchId={routeBranchId}
            custId={custId}
            latitude={form.latitude}
            longitude={form.longitude}
            locationUpdatedAt={form.locationUpdatedAt}
            locationSourceName={form.locationSourceName}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerForm;
